import React from 'react'
import Head from 'next/head'
import Link from 'next/link'
import SchemaMarkup from '@src/components/SchemaMarkup'
import classes from '@src/styles/blogPost.module.css'
import type { Post, PostSummary } from '@src/functions/blog'

const SITE = 'https://getdimension.com'

function formatDate(iso: string) {
  // Fixed locale and UTC: the server and the browser must agree or React
  // hydration will flag a mismatch.
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export type BlogPostProps = {
  post: Post
  related?: PostSummary[]
}

/**
 * Renders a file-backed blog post with the metadata the WordPress template
 * never emitted: canonical URL, BlogPosting schema, author, published and
 * modified dates, breadcrumbs, and real internal links to service pages.
 */
/**
 * The quote button shown under the byline.
 *
 * Defaults to the first related service so a post only has to declare
 * `relatedServices` to get a working CTA — one less thing for the blog bot to
 * get wrong. `cta: false` opts out; an explicit `cta` overrides the default.
 * It points at the service page rather than straight to the booking app, so the
 * reader lands on pricing and FAQs first and the post passes internal link
 * equity to a page we want ranking.
 */
function resolveCta(post: Post) {
  if (post.cta === false) return null
  const href = post.cta?.href ?? post.relatedServices?.[0]?.href
  if (!href) return null
  return { href, label: post.cta?.label ?? 'Get an instant quote' }
}

function BlogPost({ post, related = [] }: BlogPostProps) {
  const url = `${SITE}/blog/${post.slug}`
  const cta = resolveCta(post)
  const published = post.date
  const modified = post.updated ?? post.date
  const image = post.heroImage?.startsWith('http')
    ? post.heroImage
    : post.heroImage && `${SITE}${post.heroImage}`

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    datePublished: published,
    dateModified: modified,
    author: { '@type': 'Person', name: post.author },
    publisher: {
      '@type': 'Organization',
      name: 'Dimension',
      url: SITE,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE}/images/dimension-logo.svg`,
      },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    ...(image ? { image } : {}),
    ...(post.tags?.length ? { keywords: post.tags.join(', ') } : {}),
  }

  const breadcrumbs = {
    items: [
      { name: 'Home', url: SITE },
      { name: 'Blog', url: `${SITE}/blog` },
      { name: post.title, url },
    ],
  }

  return (
    <>
      <Head>
        <title>{`${post.title} | Dimension`}</title>
        <meta name="description" content={post.description} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href={url} />
        <meta name="author" content={post.author} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:url" content={url} />
        {image && <meta property="og:image" content={image} />}
        <meta property="article:published_time" content={published} />
        <meta property="article:modified_time" content={modified} />
        <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema, null, 2) }}
        />
        <SchemaMarkup type="BreadcrumbList" data={breadcrumbs} />
      </Head>

      <article className={classes.root}>
        {/* The archive template ships a plain "Back to blog" link and readers
            expect it here too. The breadcrumb trail stays alongside it: it is
            what the BreadcrumbList markup describes, and Google prefers the
            visible trail to match. */}
        <nav className={classes.topNav} aria-label="Breadcrumb">
          <Link href="/blog" className={classes.backLink}>
            <span aria-hidden="true">←</span> Back to blog
          </Link>
          <span className={classes.breadcrumbs}>
            <Link href="/">Home</Link>
            <span aria-hidden="true">/</span>
            <Link href="/blog">Blog</Link>
          </span>
        </nav>

        <header className={classes.header}>
          {post.category && <p className={classes.category}>{post.category}</p>}
          <h1 className={classes.title}>{post.title}</h1>
          <p className={classes.description}>{post.description}</p>
          <p className={classes.byline}>
            By {post.author}
            <span aria-hidden="true"> · </span>
            <time dateTime={published}>{formatDate(published)}</time>
            {post.updated && post.updated !== post.date && (
              <>
                <span aria-hidden="true"> · </span>
                <span>Updated {formatDate(post.updated)}</span>
              </>
            )}
            <span aria-hidden="true"> · </span>
            {post.readingMinutes} min read
          </p>

          {cta && (
            <Link href={cta.href} className={classes.cta}>
              {cta.label}
              <span aria-hidden="true">→</span>
            </Link>
          )}
        </header>

        {post.heroImage && (
          // eslint-disable-next-line @next/next/no-img-element
          <img className={classes.hero} src={post.heroImage} alt={post.heroAlt ?? ''} />
        )}

        <div
          className={classes.body}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: post.contentHtml }}
        />

        {post.relatedServices && post.relatedServices.length > 0 && (
          <aside className={classes.services}>
            <h2 className={classes.asideHeading}>Services mentioned in this article</h2>
            <ul className={classes.serviceList}>
              {post.relatedServices.map((service) => (
                <li key={service.href}>
                  <Link href={service.href}>{service.label}</Link>
                </li>
              ))}
            </ul>
          </aside>
        )}

        {related.length > 0 && (
          <aside className={classes.related}>
            <h2 className={classes.asideHeading}>More from the blog</h2>
            <ul className={classes.relatedList}>
              {related.map((item) => (
                <li key={item.slug}>
                  <Link href={`/blog/${item.slug}`}>{item.title}</Link>
                </li>
              ))}
            </ul>
          </aside>
        )}

        {/* Repeated at the end: after a long read the header link is well off
            screen, and this is where a reader who finished is deciding where to
            go next. */}
        <nav className={classes.bottomNav}>
          <Link href="/blog" className={classes.backLink}>
            <span aria-hidden="true">←</span> Back to blog
          </Link>
        </nav>
      </article>
    </>
  )
}

export default BlogPost
